import { type ReactElement, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage, usageToElementType } from '@constants'
import { AxiosError } from 'axios'
import { find, invert } from 'lodash-es'
import { Box, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { Autocomplete, Label } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useSystemManagerStore } from '@editorStores'
import { useProjectEarthMassForce, useResultsInvalidation } from '@editorHooks'
import { getModel, getProjectAssemblies } from '@queries'
import { createProjectAssembly, editProjectAssembly, updateElementAssemblies } from '@mutations'
import { CreateDialog, EditAssemblyDialog, assemblySchema } from 'src/components/manager/assemblies'
import { getDefaultShortName } from 'src/utils/assemblies'

export type AssemblyBlockProps = {
  surfaceGuid: string
  assemblyElementType: 'slabs' | 'roof_slabs'
  selectedAssemblyGuid: string | null
  disableForm: boolean
}

const AssemblyBlock = ({
  surfaceGuid,
  assemblyElementType,
  selectedAssemblyGuid,
  disableForm,
}: AssemblyBlockProps): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const earthMassForce = useProjectEarthMassForce()
  const invalidateResults = useResultsInvalidation()
  const [createValues, setCreateValues] = useState<Assembly | null>(null)
  const [editValues, setEditValues] = useState<Assembly | null>(null)

  const assemblies = useSystemManagerStore(state => state.assemblies)

  const assemblyOptions = useMemo(
    () =>
      assemblies.reduce<{ value: string; label: string }[]>((acc, assembly) => {
        if (usageToElementType[assembly.usage_type] !== assemblyElementType) return acc
        return [...acc, { value: assembly.guid, label: assembly.name }]
      }, []),
    [assemblies, assemblyElementType],
  )

  // Not a Form2: this block lives inside the detail pane's design Form2, and a
  // nested <form> is invalid HTML — so the form context is provided without a
  // form element and saving goes through onClick. No validation schema here,
  // so nothing else from Form2 is lost.
  const methods = useForm({ defaultValues: { selectedAssemblyGuid } })

  // The mutations below differ only in their messages and in whether the list of
  // project assemblies itself changed -- assigning one leaves the list untouched.
  const mutationOptions = ({
    successMessage,
    errorMessage,
    assembliesChanged = false,
  }: {
    successMessage: string
    errorMessage: string
    assembliesChanged?: boolean
  }) => ({
    onSuccess: async () => {
      if (assembliesChanged)
        await queryClient.invalidateQueries(getProjectAssemblies.getKey(projectId))
      await queryClient.invalidateQueries(getModel.getKey(projectId))
      await invalidateResults(projectId as string)
      enqueueSnackbar(successMessage, { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, errorMessage), { variant: 'error' })
    },
  })

  const { mutateAsync, isLoading } = useMutation(
    (data: { element_guids: string[]; assembly_guid: string }) =>
      updateElementAssemblies.request(projectId as string, data),
    mutationOptions({
      successMessage: 'Aufbauzuweisung erfolgreich gespeichert',
      errorMessage: 'Fehler beim Speichern der Aufbauzuweisung',
    }),
  )

  const createMutation = useMutation(
    (data: Assembly) => createProjectAssembly.request(projectId as string, data),
    mutationOptions({
      successMessage: 'Aufbau erfolgreich angelegt',
      errorMessage: 'Fehler beim Speichern des Aufbaus',
      assembliesChanged: true,
    }),
  )

  const editMutation = useMutation(
    (data: Assembly) => editProjectAssembly.request(projectId as string, data),
    mutationOptions({
      successMessage: 'Aufbau erfolgreich gespeichert',
      errorMessage: 'Fehler beim Speichern des Aufbaus',
      assembliesChanged: true,
    }),
  )

  const onSave = methods.handleSubmit(async values => {
    if (!projectId || !values.selectedAssemblyGuid) return
    await mutateAsync({
      element_guids: [surfaceGuid],
      assembly_guid: values.selectedAssemblyGuid,
    })
  })

  return (
    <FormProvider {...methods}>
      <Box p={1} border={1} borderColor="grey.200" borderRadius={1}>
        <Stack spacing={1}>
          <Label>Aufbau</Label>
          <Autocomplete
            name="selectedAssemblyGuid"
            options={assemblyOptions}
            onEdit={({ value }) => setEditValues(find(assemblies, ['guid', value]) || null)}
            onAdd={() => {
              const usageType = invert(usageToElementType)[assemblyElementType] as AssemblyTypes
              setCreateValues({
                ...assemblySchema.getDefault(),
                kind: assemblySchema.getDefault().kind as AssemblyKind,
                usage_type: usageType,
                isStandard: false,
                short_name: getDefaultShortName(usageType, assemblies),
              })
            }}
            onAddLabel="Neuen Aufbau anlegen"
            dataCy="select-assembly-autocomplete"
            autoFocus={false}
            textFieldProps={{ disabled: disableForm }}
          />
          <Stack direction="row" justifyContent="end">
            <SaveButton
              loading={isLoading}
              disabled={disableForm || !methods.formState.isDirty}
              size="small"
              onClick={() => void onSave()}
              data-cy="submit-assembly-assignment"
            >
              Speichern
            </SaveButton>
          </Stack>
        </Stack>
      </Box>
      {createValues && (
        <CreateDialog
          mutation={createMutation}
          open
          onClose={() => setCreateValues(null)}
          assembly={createValues}
          earthMassForce={earthMassForce}
        />
      )}
      {editValues && (
        <EditAssemblyDialog
          mutation={editMutation}
          open
          onClose={() => setEditValues(null)}
          assembly={editValues}
          earthMassForce={earthMassForce}
        />
      )}
    </FormProvider>
  )
}

export default AssemblyBlock
